import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, setCsrfToken } from '../api';

const PW_RULES = [
  { key: 'length',  label: '8+ characters',    test: pw => pw.length >= 8 },
  { key: 'upper',   label: 'Uppercase letter',  test: pw => /[A-Z]/.test(pw) },
  { key: 'lower',   label: 'Lowercase letter',  test: pw => /[a-z]/.test(pw) },
  { key: 'number',  label: 'Number',            test: pw => /[0-9]/.test(pw) },
  { key: 'special', label: 'Special character', test: pw => /[^A-Za-z0-9]/.test(pw) },
];

function PasswordRequirements({ password }) {
  if (!password) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8 }}>
      {PW_RULES.map(r => {
        const met = r.test(password);
        return (
          <span key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
            color: met ? '#4ade80' : 'rgba(255,255,255,0.3)', transition: 'color 0.15s' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%',
              background: met ? '#4ade80' : 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
            {r.label}
          </span>
        );
      })}
    </div>
  );
}

// ── AuthModal ──────────────────────────────────────────────────────────────────
// Login + signup in a single overlay. Calls onSuccess(user) on auth.
// initialTab: 'login' | 'signup'
export default function AuthModal({ initialTab = 'login', onSuccess, onClose }) {
  const [tab,      setTab]      = useState(initialTab);
  const [error,    setError]    = useState('');
  const [busy,     setBusy]     = useState(false);

  // Shared
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Signup-only
  const [displayName, setDisplayName] = useState('');
  const [email,       setEmail]       = useState('');
  const [confirm,     setConfirm]     = useState('');

  const firstRef = useRef(null);

  useEffect(() => {
    setError('');
    setUsername(''); setPassword('');
    setDisplayName(''); setEmail(''); setConfirm('');
  }, [tab]);

  useEffect(() => {
    firstRef.current?.focus();
  }, [tab]);

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleLogin(e) {
    e.preventDefault();
    if (busy || !username.trim() || !password) return;
    setBusy(true); setError('');
    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (res.ok) { const data = await res.json(); setCsrfToken(data.csrf_token); onSuccess(data); return; }
      const body = await res.json().catch(() => ({}));
      // Dev-mode diagnostic: log exact status + payload so failures are unambiguous
      if (process.env.NODE_ENV !== 'production') {
        console.error('[login] failed — status:', res.status, '| payload:', body);
      }
      if      (res.status === 401) setError(body.error || 'Invalid username or password.');
      else if (res.status === 403) setError(body.error || 'Access denied. Contact your system administrator.');
      else if (res.status === 429) setError('Too many attempts. Please wait and try again.');
      else                         setError(`Server error (${res.status}) — please try again.`);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[login] network/fetch error:', err);
      }
      setError('Could not reach the server.');
    }
    setBusy(false);
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (busy || !username.trim() || !password) return;
    if (!displayName.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim())       { setError('Please enter your email address.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)             { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(password))         { setError('Password must include at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(password))         { setError('Password must include at least one lowercase letter.'); return; }
    if (!/[0-9]/.test(password))         { setError('Password must include at least one number.'); return; }
    if (!/[^A-Za-z0-9]/.test(password))  { setError('Password must include at least one special character.'); return; }
    setBusy(true); setError('');
    try {
      const res = await apiFetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username:     username.trim(),
          password,
          display_name: displayName.trim() || undefined,
          email:        email.trim()       || undefined,
        }),
      });
      if (res.ok) { onSuccess(await res.json()); return; }
      const body = await res.json().catch(() => ({}));
      if      (res.status === 409) setError(body.error || 'That username is already taken.');
      else if (res.status === 429) setError('Too many attempts. Please wait and try again.');
      else                         setError(body.error || 'Could not create account. Please try again.');
    } catch (_) { setError('Could not reach the server.'); }
    setBusy(false);
  }

  return (
    <div className="lp-modal-overlay">
      <div className="lp-modal" role="dialog" aria-modal="true">
        <button className="lp-modal-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="lp-modal-logo">
          <img src="/logo.svg" alt="" className="lp-modal-logo-img" />
          3<em>AI</em>migos
        </div>

        <div className="lp-modal-tabs" role="tablist">
          <button
            className={`lp-modal-tab${tab === 'login'  ? ' lp-modal-tab--active' : ''}`}
            onClick={() => setTab('login')}
            role="tab"
          >
            Sign In
          </button>
          <button
            className={`lp-modal-tab${tab === 'signup' ? ' lp-modal-tab--active' : ''}`}
            onClick={() => setTab('signup')}
            role="tab"
          >
            Create Account
          </button>
        </div>

        {/* ── Login form ──────────────────────────────────────────────────── */}
        {tab === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Username</label>
              <input
                ref={firstRef}
                className="lp-modal-input"
                type="text"
                placeholder="your-username"
                value={username}
                autoComplete="username"
                onChange={e => setUsername(e.target.value)}
              />
            </div>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Password</label>
              <input
                className="lp-modal-input"
                type="password"
                placeholder="••••••••"
                value={password}
                autoComplete="current-password"
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            {error && <div className="lp-modal-error">{error}</div>}
            <button
              className="lp-modal-submit"
              type="submit"
              disabled={busy || !username.trim() || !password}
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
            <p className="lp-modal-footnote">
              No account?{' '}
              <button
                type="button"
                className="login-gate-switch-btn"
                style={{ fontSize: 'inherit', color: '#3b82f6' }}
                onClick={() => setTab('signup')}
              >
                Create one free
              </button>
            </p>
          </form>
        )}

        {/* ── Signup form ─────────────────────────────────────────────────── */}
        {tab === 'signup' && (
          <form onSubmit={handleSignup}>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Name</label>
              <input
                ref={firstRef}
                className="lp-modal-input"
                type="text"
                placeholder="Jane Smith"
                value={displayName}
                autoComplete="name"
                onChange={e => setDisplayName(e.target.value)}
              />
            </div>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Email</label>
              <input
                className="lp-modal-input"
                type="email"
                placeholder="jane@agency.com"
                value={email}
                autoComplete="email"
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Username</label>
              <input
                className="lp-modal-input"
                type="text"
                placeholder="jsmith"
                value={username}
                autoComplete="username"
                onChange={e => setUsername(e.target.value)}
              />
            </div>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Password</label>
              <input
                className="lp-modal-input"
                type="password"
                placeholder="••••••••"
                value={password}
                autoComplete="new-password"
                onChange={e => setPassword(e.target.value)}
              />
              <PasswordRequirements password={password} />
            </div>
            <div className="lp-modal-field">
              <label className="lp-modal-label">Confirm Password</label>
              <input
                className="lp-modal-input"
                type="password"
                placeholder="••••••••"
                value={confirm}
                autoComplete="new-password"
                onChange={e => setConfirm(e.target.value)}
              />
            </div>
            {error && <div className="lp-modal-error">{error}</div>}
            <button
              className="lp-modal-submit"
              type="submit"
              disabled={busy || !displayName.trim() || !email.trim() || !username.trim() || !password || !confirm}
            >
              {busy ? 'Creating account…' : 'Create Free Account'}
            </button>
            <p className="lp-modal-footnote">
              Already have an account?{' '}
              <button
                type="button"
                className="login-gate-switch-btn"
                style={{ fontSize: 'inherit', color: '#3b82f6' }}
                onClick={() => setTab('login')}
              >
                Sign in
              </button>
              <br />
              <span>By creating an account, you get role: standard access.</span>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
